import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';
import type { Me } from './types';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Re-reads /api/auth/me (after login/signup). */
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api<Me>('/api/auth/me', { noRedirect: true });
      setMe(data);
      return data;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST', body: {}, noRedirect: true });
    } finally {
      setMe(null);
    }
  }, []);

  const isAdmin = !!me && me.user.role !== 'viewer';
  return <AuthContext.Provider value={{ me, loading, refresh, logout, isAdmin }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** For pages rendered inside the authenticated layout. */
export function useMe(): Me & { isAdmin: boolean } {
  const { me, isAdmin } = useAuth();
  if (!me) throw new Error('useMe without session');
  return { ...me, isAdmin };
}
